import { Preferences } from '@capacitor/preferences';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { CapacitorReadNativeSetting } from 'capacitor-read-native-setting';
import NativeAppConfig from '@/config/native-app.config.json';
import { useSharedStore } from '@/stores/shared/sharedStore';

interface CachedImageMeta {
	filePath: string;
	originalUrl: string;
}

/**
 * Get tenant name for cache key scoping
 */
const getTenantName = async (): Promise<string> => {
	try {
		// Try to get from Preferences first
		const tenantDetails = await Preferences.get({ key: 'tenantDetails' });
		if (tenantDetails?.value) {
			const parsed = JSON.parse(tenantDetails.value);
			if (parsed?.tenant_name) {
				return parsed.tenant_name;
			}
		}

		// Fallback to native settings
		if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('CapacitorReadNativeSetting')) {
			const tenantNameFromNative = await CapacitorReadNativeSetting.read({ key: 'tenant_name' });
			if (tenantNameFromNative?.value) {
				return tenantNameFromNative.value;
			}
		}

		// Fallback to window object
		if (window.tenantName && typeof window.tenantName === 'string' && !window.tenantName.includes('REPLACE')) {
			return window.tenantName;
		}

		// Final fallback to config
		return NativeAppConfig.TENANT_NAME || 'default';
	} catch (error) {
		console.error('Error getting tenant name for cache key:', error);
		return 'default';
	}
};

/**
 * Generate tenant-specific cache key for images
 */
const getTenantScopedCacheKey = async (imageId: string, imageName: string): Promise<string> => {
	const tenantName = await getTenantName();
	const sanitizedTenantName = tenantName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
	const cleanName = imageName.split('?')[0];
	return `${imageId}-${sanitizedTenantName}-${cleanName}`;
};

/**
 * Generate a safe filename from the cache key
 */
const toSafeFileName = (cacheKey: string): string => {
	return cacheKey.replace(/[^a-zA-Z0-9_-]/g, '_');
};

/**
 * Get a WebView-accessible URL for a cached file
 */
const getWebViewUrl = async (filePath: string): Promise<string> => {
	const uriResult = await Filesystem.getUri({
		path: filePath,
		directory: Directory.Cache,
	});
	return Capacitor.convertFileSrc(uriResult.uri);
};

export const useCacheImage = () => {
	const sharedStore = useSharedStore();

	// Set image to cache
	const setImageToCache = async (imageId: string, imageName: string, imageUrl: string): Promise<string> => {
		if (!navigator.onLine) {
			return imageUrl;
		}

		// On web, just return the original URL — browser handles caching natively
		if (!Capacitor.isNativePlatform()) {
			return imageUrl;
		}

		try {
			const cacheKey = await getTenantScopedCacheKey(imageId, imageName);
			const filePath = `cached_images/${toSafeFileName(cacheKey)}`;

			// Download directly to disk (no JS bridge transfer)
			await Filesystem.downloadFile({
				url: imageUrl,
				path: filePath,
				directory: Directory.Cache,
			});

			// Store only small metadata in Preferences
			const meta: CachedImageMeta = { filePath, originalUrl: imageUrl };
			await Preferences.set({
				key: cacheKey,
				value: JSON.stringify(meta),
			});

			return await getWebViewUrl(filePath);
		} catch (error) {
			console.error('Error setting image to cache', imageName, imageUrl, error);
			return imageUrl;
		}
	};

	// Load image from cache or set it to cache
	const loadImage = async (imageId: string, imageName: string, imageUrl: string, updatedAt?: string): Promise<string> => {
		if (!Capacitor.isNativePlatform()) {
			return imageUrl;
		}

		const bustedImageUrl = updatedAt ? `${imageUrl}?v=${updatedAt}` : imageUrl;
		const cacheKey = await getTenantScopedCacheKey(imageId, imageName);

		try {
			// Watch for network changes to update image
			const onlineEventListeners = sharedStore.onlineEventListeners as string[];
			if (!onlineEventListeners.includes(imageName)) {
				window.addEventListener('online', async () => {
					await setImageToCache(imageId, imageName, bustedImageUrl);
				});
				onlineEventListeners.push(imageName);
			}

			const stored = await Preferences.get({ key: cacheKey });

			if (stored?.value) {
				// Try to parse as JSON metadata
				let meta: CachedImageMeta | null = null;
				try {
					const parsed = JSON.parse(stored.value);
					if (parsed?.filePath) {
						meta = parsed as CachedImageMeta;
					}
				} catch {
					// Not JSON — old base64 format, treat as cache miss
				}

				if (meta) {
					if (meta.originalUrl !== bustedImageUrl) {
						if (!navigator.onLine) {
							try {
								await Filesystem.stat({ path: meta.filePath, directory: Directory.Cache });
								return await getWebViewUrl(meta.filePath);
							} catch {
								return bustedImageUrl;
							}
						}
						return await setImageToCache(imageId, imageName, bustedImageUrl);
					}

					try {
						await Filesystem.stat({
							path: meta.filePath,
							directory: Directory.Cache,
						});
						return await getWebViewUrl(meta.filePath);
					} catch {
						// File was cleaned up, re-download
					}
				}

				// Old format or missing file — re-download (overwrites old Preferences entry)
			}

			// No cache or cache miss — download fresh
			return await setImageToCache(imageId, imageName, imageUrl);
		} catch (error) {
			console.error('Error getting image from cache', imageName, imageUrl, error);
			return imageUrl;
		}
	};

	return {
		loadImage,
		setImageToCache,
	};
};
